/**
 * AIPlayer - AI logic for the Path Game with multiple difficulty levels
 */

import type { Point, GameState, Move } from './types';
import {
  distance,
  applyMoveToState,
  getValidMovesForState,
} from './gameUtils';

export type AIDifficulty = 'easy' | 'medium' | 'hard' | 'expert';

// ============== Heuristic Scoring ==============

/** Score a move based on heuristics (used by medium, hard, expert) */
function scoreMove(move: Move, gridSize: number): number {
  const center = (gridSize - 1) / 2;
  const moveDist = move.dist ?? distance(move.from, move.to);
  
  let score = 0;
  
  // Prefer shorter moves to preserve longer options for later
  score -= moveDist * 100;
  
  // Prefer central ending positions
  const centerDist = Math.abs(move.to.x - center) + Math.abs(move.to.y - center);
  score -= centerDist * 10;
  
  // For first move, strongly prefer length 1 (adjacent) moves
  if (move.isFirstMove && moveDist <= 1.5) {
    score += 500;
  }
  
  return score;
}

/** Get top N candidate moves by heuristic score */
function getTopCandidates(moves: Move[], gridSize: number, limit: number): Move[] {
  if (moves.length <= limit) return moves;
  
  const scored = moves.map(move => ({ move, score: scoreMove(move, gridSize) }));
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map(s => s.move);
}

// ============== State Evaluation ==============

/** Evaluate a game state for minimax */
function evaluateState(state: GameState, aiPlayer: 1 | 2): number {
  const opponent: 1 | 2 = aiPlayer === 1 ? 2 : 1;
  
  const aiMoves = getValidMovesForState(state, aiPlayer);
  const oppMoves = getValidMovesForState(state, opponent);
  
  // Terminal states
  if (state.currentPlayer === aiPlayer && aiMoves.length === 0) return -10000;
  if (state.currentPlayer === opponent && oppMoves.length === 0) return 10000;
  
  let score = 0;
  const center = (state.gridSize - 1) / 2;
  const aiPlayerState = state.players[aiPlayer];
  const oppPlayerState = state.players[opponent];
  
  // Mobility advantage
  score += (aiMoves.length - oppMoves.length) * 10;
  
  // Position quality - prefer center
  if (aiPlayerState.pos) {
    score -= (Math.abs(aiPlayerState.pos.x - center) + Math.abs(aiPlayerState.pos.y - center)) * 2;
  }
  if (oppPlayerState.pos) {
    score += (Math.abs(oppPlayerState.pos.x - center) + Math.abs(oppPlayerState.pos.y - center)) * 2;
  }
  
  // Last distance - lower is better (more future options)
  score -= aiPlayerState.lastDist * 5;
  score += oppPlayerState.lastDist * 5;
  
  return score;
}

// ============== Minimax Algorithm ==============

function minimax(
  state: GameState,
  depth: number,
  aiPlayer: 1 | 2,
  isMaximizing: boolean,
  alpha: number,
  beta: number,
  usePruning: boolean
): number {
  const currentPlayer = state.currentPlayer;
  const moves = getValidMovesForState(state, currentPlayer);
  
  if (depth === 0 || moves.length === 0) {
    return evaluateState(state, aiPlayer);
  }
  
  if (isMaximizing) {
    let maxEval = -Infinity;
    for (const move of moves) {
      const newState = applyMoveToState(state, move, currentPlayer);
      const evaluation = minimax(newState, depth - 1, aiPlayer, false, alpha, beta, usePruning);
      maxEval = Math.max(maxEval, evaluation);
      if (usePruning) {
        alpha = Math.max(alpha, evaluation);
        if (beta <= alpha) break;
      }
    }
    return maxEval;
  } else {
    let minEval = Infinity;
    for (const move of moves) {
      const newState = applyMoveToState(state, move, currentPlayer);
      const evaluation = minimax(newState, depth - 1, aiPlayer, true, alpha, beta, usePruning);
      minEval = Math.min(minEval, evaluation);
      if (usePruning) {
        beta = Math.min(beta, evaluation);
        if (beta <= alpha) break;
      }
    }
    return minEval;
  }
}

/** Find best move using minimax */
function findBestMove(
  state: GameState,
  playerNum: 1 | 2,
  depth: number,
  usePruning: boolean,
  candidateLimit: number
): Move | null {
  const moves = getValidMovesForState(state, playerNum);
  if (moves.length === 0) return null;
  
  const candidates = getTopCandidates(moves, state.gridSize, candidateLimit);
  let bestMove = candidates[0];
  let bestScore = -Infinity;
  let alpha = -Infinity;
  
  for (const move of candidates) {
    const newState = applyMoveToState(state, move, playerNum);
    const score = minimax(newState, depth, playerNum, false, alpha, Infinity, usePruning);
    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }
    if (usePruning) {
      alpha = Math.max(alpha, score);
    }
  }
  
  return bestMove;
}

// ============== AI Difficulty Implementations ==============

/** Easy: Pick a random valid move */
function getEasyMove(state: GameState, playerNum: 1 | 2): Move | null {
  const moves = getValidMovesForState(state, playerNum);
  if (moves.length === 0) return null;
  return moves[Math.floor(Math.random() * moves.length)];
}

/** Medium: Use heuristics with slight randomness */
function getMediumMove(state: GameState, playerNum: 1 | 2): Move | null {
  const moves = getValidMovesForState(state, playerNum);
  if (moves.length === 0) return null;
  
  const scored = moves.map(move => ({ move, score: scoreMove(move, state.gridSize) }));
  scored.sort((a, b) => b.score - a.score);
  
  // Pick randomly from top moves with similar scores
  const topMoves = scored.filter(m => m.score >= scored[0].score - 10);
  return topMoves[Math.floor(Math.random() * Math.min(topMoves.length, 2))].move;
}

/** Hard: 2-ply minimax */
function getHardMove(state: GameState, playerNum: 1 | 2): Move | null {
  return findBestMove(state, playerNum, 2, false, 20);
}

/** Expert: Alpha-beta pruning with adaptive depth */
function getExpertMove(state: GameState, playerNum: 1 | 2): Move | null {
  const depth = state.allPoints.length < 10 ? 4 : 3;
  return findBestMove(state, playerNum, depth, true, 15);
}

// ============== Main AI Interface ==============

/**
 * Get the best move for an AI player
 */
export function getAIMove(
  gameState: GameState,
  playerNum: 1 | 2,
  difficulty: AIDifficulty = 'medium'
): Move | null {
  switch (difficulty) {
    case 'easy': return getEasyMove(gameState, playerNum);
    case 'medium': return getMediumMove(gameState, playerNum);
    case 'hard': return getHardMove(gameState, playerNum);
    case 'expert': return getExpertMove(gameState, playerNum);
    default: return getMediumMove(gameState, playerNum);
  }
}

/**
 * AIPlayer class for object-oriented usage
 */
export class AIPlayer {
  private difficulty: AIDifficulty;

  constructor(difficulty: AIDifficulty = 'medium') {
    this.difficulty = difficulty;
  }

  getMove(gameState: GameState, playerNum: 1 | 2): Move | null {
    return getAIMove(gameState, playerNum, this.difficulty);
  }

  setDifficulty(difficulty: AIDifficulty): void {
    this.difficulty = difficulty;
  }

  getDifficulty(): AIDifficulty {
    return this.difficulty;
  }
}
